// Role permissions: what each role is allowed to do. The defaults below are what the app shipped with;
// an administrator can change any of them on the Roles & Access screen (stored in the role_permissions
// table). Used by both the server (to enforce) and the browser (to show or hide things).

import type { UserRole } from './types.ts';

export type PermissionKey =
  | 'task.approve'
  | 'task.create'
  | 'task.edit'
  | 'task.comment'
  | 'task.delete'
  | 'task.delete_permanent'
  | 'project.view_all'
  | 'project.edit'
  | 'project.members'
  | 'project.structure'
  | 'project.delete_items'
  | 'project.create'
  | 'project.delete'
  | 'team.create'
  | 'team.edit'
  | 'team.members'
  | 'team.delete'
  | 'meetings.schedule'
  | 'meetings.manage'
  | 'users.manage'
  | 'security.view'
  | 'roles.manage';

export interface PermissionDefinition {
  key: PermissionKey;
  group: string;
  label: string;
  description: string;
  /** Only the administrator can ever hold it (it controls who can change access itself) */
  adminOnly?: boolean;
}

export const PERMISSIONS: PermissionDefinition[] = [
  {
    key: 'task.approve',
    group: 'Tasks',
    label: 'Complete & review tasks',
    description: 'Move a task to Completed, approve work in review, send it back for rework, and reopen completed tasks.',
  },
  {
    key: 'task.create',
    group: 'Tasks',
    label: 'Create tasks',
    description: 'Create tasks and import them from a spreadsheet.',
  },
  {
    key: 'task.edit',
    group: 'Tasks',
    label: 'Edit & move tasks',
    description: 'Edit task details, assign people, drag cards between columns and move tasks through To Do, In Progress and In Review. Without it a role is read-only on tasks.',
  },
  {
    key: 'task.comment',
    group: 'Tasks',
    label: 'Comment on tasks',
    description: 'Post and reply to comments on tasks.',
  },
  {
    key: 'task.delete',
    group: 'Tasks',
    label: 'Delete tasks (to the Bin)',
    description: 'Move tasks to the Bin and restore them.',
  },
  {
    key: 'task.delete_permanent',
    group: 'Tasks',
    label: 'Delete tasks permanently',
    description: 'Permanently remove a task from the Bin. The person who created a task can always remove their own.',
  },
  {
    key: 'project.view_all',
    group: 'Projects',
    label: 'See every project & task',
    description: 'See all projects, tasks and dashboards, not just the projects the person belongs to.',
  },
  {
    key: 'project.edit',
    group: 'Projects',
    label: 'Edit project settings',
    description: 'Change a project name, dates, status and review rules. A project owner or lead can always edit their own project.',
  },
  {
    key: 'project.members',
    group: 'Projects',
    label: 'Manage project members',
    description: 'Add or remove people on a project. A project owner or lead can always manage the members of their own project.',
  },
  {
    key: 'project.structure',
    group: 'Projects',
    label: 'Manage lists, builds & features',
    description: 'Create and edit board lists (columns), builds and features.',
  },
  {
    key: 'project.delete_items',
    group: 'Projects',
    label: 'Delete lists, builds & features',
    description: 'Delete board lists, builds and features. A project owner or lead can always do this in their own project.',
  },
  {
    key: 'project.create',
    group: 'Projects',
    label: 'Create projects & boards',
    description: 'Create and duplicate projects and boards.',
  },
  {
    key: 'project.delete',
    group: 'Projects',
    label: 'Delete projects',
    description: 'Permanently delete a project and its board.',
  },
  {
    key: 'team.create',
    group: 'Teams',
    label: 'Create teams',
    description: 'Create a new team with its #team chat.',
  },
  {
    key: 'team.edit',
    group: 'Teams',
    label: 'Edit teams',
    description: 'Change any team’s name, description, privacy and linked boards.',
  },
  {
    key: 'team.members',
    group: 'Teams',
    label: 'Manage team members',
    description: 'Add or remove members of any team and make someone a team lead.',
  },
  {
    key: 'team.delete',
    group: 'Teams',
    label: 'Delete teams',
    description: 'Delete a team and archive its chat.',
  },
  {
    key: 'meetings.schedule',
    group: 'Meetings',
    label: 'Schedule meetings',
    description: 'Create scheduled and instant meetings and invite people.',
  },
  {
    key: 'meetings.manage',
    group: 'Meetings',
    label: 'Manage anyone’s meetings',
    description: 'Reschedule, cancel or delete meetings hosted by someone else.',
  },
  {
    key: 'users.manage',
    group: 'People',
    label: 'Manage users',
    description: 'Add or remove users, reset passwords, verify accounts and change roles (only an admin can create or edit an admin).',
  },
  {
    key: 'security.view',
    group: 'Security',
    label: 'Open the Security screen',
    description: 'See the security audit trail and the user credentials screen.',
  },
  {
    key: 'roles.manage',
    group: 'Security',
    label: 'Change role permissions',
    description: 'Edit this screen. Kept for administrators only.',
    adminOnly: true,
  },
];

/** Roles shown as columns on the Roles & Access screen, in order */
export const CONFIGURABLE_ROLES: UserRole[] = ['admin', 'manager', 'team_lead', 'member', 'sqa', 'guest', 'viewer'];

export const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  manager: 'Manager',
  team_lead: 'Team Lead',
  member: 'Member',
  sqa: 'SQA',
  guest: 'Guest',
  viewer: 'Viewer',
  limited_member: 'Member',
};

export type PermissionMap = Record<PermissionKey, UserRole[]>;

export const DEFAULT_PERMISSION_MAP: PermissionMap = {
  'task.approve': ['admin', 'manager', 'team_lead', 'sqa'],
  'task.create': ['admin', 'manager', 'team_lead', 'member', 'sqa'],
  'task.edit': ['admin', 'manager', 'team_lead', 'member', 'sqa'],
  'task.comment': ['admin', 'manager', 'team_lead', 'member', 'sqa', 'guest'],
  'task.delete': ['admin', 'manager', 'team_lead', 'member', 'sqa'],
  'task.delete_permanent': ['admin', 'manager'],
  'project.view_all': ['admin'],
  'project.edit': ['admin', 'manager'],
  'project.members': ['admin', 'manager'],
  'project.structure': ['admin', 'manager', 'team_lead', 'member', 'sqa'],
  'project.delete_items': ['admin', 'manager'],
  'project.create': ['admin', 'manager', 'team_lead'],
  'project.delete': ['admin', 'manager'],
  'team.create': ['admin'],
  'team.edit': ['admin'],
  'team.members': ['admin'],
  'team.delete': ['admin'],
  'meetings.schedule': ['admin', 'manager', 'team_lead', 'member', 'sqa'],
  'meetings.manage': ['admin', 'team_lead'],
  'users.manage': ['admin'],
  'security.view': ['admin'],
  'roles.manage': ['admin'],
};

export const isPermissionKey = (key: unknown): key is PermissionKey =>
  PERMISSIONS.some((p) => p.key === key);

/** The administrator can never lose a permission, so nobody can lock everyone out */
export const isLockedCell = (key: PermissionKey, role: string) => {
  if (role === 'admin') return true;
  const def = PERMISSIONS.find((p) => p.key === key);
  return Boolean(def?.adminOnly);
};

const cloneMap = (map: PermissionMap): PermissionMap =>
  Object.fromEntries(Object.entries(map).map(([k, v]) => [k, [...v]])) as PermissionMap;

// The map in use. Both sides set it after loading (server: from the database, browser: from the API),
// so plain helper functions elsewhere (like the task transition rules) can ask without extra plumbing.
let activeMap: PermissionMap = cloneMap(DEFAULT_PERMISSION_MAP);

export function setPermissionMap(map: Partial<PermissionMap> | null | undefined): void {
  const next = cloneMap(DEFAULT_PERMISSION_MAP);
  if (map) {
    for (const def of PERMISSIONS) {
      const roles = map[def.key];
      if (Array.isArray(roles)) next[def.key] = [...roles];
    }
  }
  // Locked cells always hold
  for (const def of PERMISSIONS) {
    const withAdmin = new Set(next[def.key]);
    withAdmin.add('admin');
    next[def.key] = Array.from(withAdmin) as UserRole[];
    if (def.adminOnly) next[def.key] = ['admin'];
  }
  activeMap = next;
}

export function getPermissionMap(): PermissionMap {
  return cloneMap(activeMap);
}

export function roleHasPermission(role: string | null | undefined, key: PermissionKey): boolean {
  if (!role) return false;
  // "limited_member" is stored as a plain member everywhere permissions are concerned
  const effective = role === 'limited_member' ? 'member' : role;
  return activeMap[key]?.includes(effective as UserRole) ?? false;
}
